import type { QueryClient } from '@tanstack/react-query'
import { TanStackDevtools } from '@tanstack/react-devtools'
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  ScrollRestoration,
} from '@tanstack/react-router'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { Suspense } from 'react'
import { Toaster } from 'sonner'
import { MaintenanceNotice } from '@/components/feedback/maintenance-notice'
import { GoogleTagManager } from '@/components/layout/google-tag-manager'
import { ThemeProvider } from '@/components/layout/theme-provider'
import { TooltipProvider } from '@/components/ui/tooltip'
import appCss from '@/globals.css?url'
import { MAINTENANCE_MODE } from '@/lib/constants'

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  ssr: false,
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { name: 'theme-color', content: '#ED4A68' },
      { title: 'MyJunea' },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
      {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossOrigin: 'anonymous' as const,
      },
      {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap',
      },
      { rel: 'icon', href: '/favicon.svg', type: 'image/svg+xml' },
      { rel: 'manifest', href: '/manifest.json' },
      { rel: 'preload', href: '/images/machines/drinks.png', as: 'image' },
      { rel: 'preload', href: '/images/machines/snacks.png', as: 'image' },
      { rel: 'preload', href: '/images/machines/fridge.png', as: 'image' },
    ],
  }),
  component: RootComponent,
  shellComponent: RootDocument,
})

function RootComponent() {
  if (MAINTENANCE_MODE) {
    return (
      <ThemeProvider>
        <MaintenanceNotice />
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider>
      <GoogleTagManager />
      <TooltipProvider>
        <Suspense>
          <Outlet />
        </Suspense>
      </TooltipProvider>
    </ThemeProvider>
  )
}

function RootDocument({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body className="bg-background min-h-screen font-sans antialiased">
        {children}
        <Toaster position="bottom-left" richColors closeButton />
        <ScrollRestoration />
        <Scripts />
        {import.meta.env.DEV && (
          <TanStackDevtools
            config={{ position: 'bottom-right' }}
            plugins={[
              {
                name: 'TanStack Router',
                render: <TanStackRouterDevtoolsPanel />,
              },
            ]}
          />
        )}
      </body>
    </html>
  )
}
